/**
 * Web token storage (PWA + Discord). Backing priority: localStorage →
 * sessionStorage → in-memory. Tauri/Capacitor have their own stores.
 */
const KEY = 'infiltre:token:v1';
const LEGACY_KEY = 'infiltre:token:v1';

interface BackingStore {
  read(): string | null;
  write(token: string): void;
  clear(): void;
  readonly label: 'local' | 'session' | 'memory';
}

const inMemory = (): BackingStore => {
  let cached: string | null = null;
  return {
    read: () => cached,
    write: (token) => {
      cached = token;
    },
    clear: () => {
      cached = null;
    },
    label: 'memory',
  };
};

const fromStorage = (storage: Storage, label: 'local' | 'session'): BackingStore => ({
  read: () => {
    try {
      return storage.getItem(KEY);
    } catch {
      return null;
    }
  },
  write: (token) => {
    try {
      storage.setItem(KEY, token);
    } catch {
      // quota / disabled storage — degrade silently to in-memory
    }
  },
  clear: () => {
    try {
      storage.removeItem(KEY);
    } catch {
      // quota / disabled storage — degrade silently
    }
  },
  label,
});

const probeStore = (): BackingStore => {
  if (typeof localStorage !== 'undefined') {
    try {
      const probeKey = `${KEY}:probe`;
      localStorage.setItem(probeKey, '1');
      localStorage.removeItem(probeKey);
      return fromStorage(localStorage, 'local');
    } catch {
      // localStorage blocked — try sessionStorage next
    }
  }
  if (typeof sessionStorage !== 'undefined') {
    try {
      const probeKey = `${KEY}:probe`;
      sessionStorage.setItem(probeKey, '1');
      sessionStorage.removeItem(probeKey);
      return fromStorage(sessionStorage, 'session');
    } catch {
      // sessionStorage blocked — fall through to in-memory
    }
  }
  return inMemory();
};

const store = probeStore();

/** Migrate legacy sessionStorage token to localStorage on upgrade. */
const migrateLegacy = (): void => {
  if (store.label === 'session') return;
  if (store.read() !== null) return;
  if (typeof sessionStorage === 'undefined') return;
  try {
    const legacy = sessionStorage.getItem(LEGACY_KEY);
    if (legacy) {
      store.write(legacy);
      sessionStorage.removeItem(LEGACY_KEY);
    }
  } catch {
    // legacy migration is best-effort
  }
};
migrateLegacy();

const listeners = new Set<(token: string | null) => void>();

const notify = (token: string | null): void => {
  for (const cb of listeners) {
    try {
      cb(token);
    } catch {
      // listener errors must not break notification chain
    }
  }
};

export const getWebToken = (): string | null => store.read();

export const setWebToken = (token: string): void => {
  store.write(token);
  notify(token);
};

export const clearWebToken = (): void => {
  store.clear();
  notify(null);
};

/** Drives WS reconnect-with-fresh-bearer path on auth refresh. */
export const subscribeWebToken = (cb: (token: string | null) => void): (() => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

export const webTokenStoreLabel = (): 'local' | 'session' | 'memory' => store.label;
